import { Fragment, forwardRef, useState, useMemo } from "react";
import {
  Combobox,
  ComboboxOption,
  ComboboxOptions,
  ComboboxButton,
  Transition,
  ComboboxInput,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import {
  ArrowDownIcon,
  CheckIcon,
  ChevronDownIcon,
  PlusIcon,
} from "@heroicons/react/outline";
import clsx from "clsx";
import FormLabel from "./FormLabel";
import InputError from "./InputError";
import { generateId } from "../../../Utils";
import { useTranslation } from "react-i18next";

// Headless UI points a ListboxButton's aria-labelledby at the button alone and
// drops any aria-describedby handed to it, so the field label would vanish
// from the control's name. Rendering through this button puts both back.
const LabelledListboxButton = forwardRef(
  ({ labelledBy, describedBy, ...props }, ref) => (
    <button
      {...props}
      ref={ref}
      type={props.type || "button"}
      aria-labelledby={labelledBy || props["aria-labelledby"]}
      aria-describedby={describedBy}
    />
  ),
);
LabelledListboxButton.displayName = "LabelledListboxButton";

const FormSelect = ({
  label,
  required,
  hint,
  options,
  value,
  onChange,
  id: propId,
  name,
  error = null,
  isLoading = false,
  disabled = false,
  searchable = false,
  placeholder = "Select option",
  allowAdd = false,
  onAdd = () => {},
  isAdding = false,
  addError = null,
  hasMore = false,
  onLoadMore = () => {},
  loadingMore = false,
  loadMoreError = null,
  onClear,
  isClearable = false,
  maxRenderedOptions = 0,
}) => {
  const { t } = useTranslation();

  const [query, setQuery] = useState("");

  const id = propId || name || generateId();

  // Loading deliberately does not disable the combobox. A disabled Combobox
  // cannot be focused or opened, which used to swallow the first keystroke and
  // made the `common.loading` row below unreachable. The field simply looked
  // inert until the request settled. Typing now opens the panel, shows the
  // loading row, and filters as soon as the options arrive.
  const isDisabled = disabled;

  const isGrouped = options.some((option) => option.options);

  const hasError =
    error?.messages?.length ||
    error?.message ||
    (Array.isArray(error) ? error.length : error);

  const filteredOptions = useMemo(() => {
    if (!searchable || query === "") return options;

    const normalizedQuery = query.toLowerCase().replace(/\s+/g, "");

    const matchesQuery = (text) =>
      text.toLowerCase().replace(/\s+/g, "").includes(normalizedQuery);

    return options
      .filter((option) => {
        if (option.options) {
          const groupLabelMatch = matchesQuery(option.label);

          if (groupLabelMatch) {
            return true;
          }

          return option.options.some((subOption) =>
            matchesQuery(subOption.label),
          );
        }
        return matchesQuery(option.label);
      })
      .map((option) => {
        if (option.options) {
          const groupLabelMatch = matchesQuery(option.label);

          if (groupLabelMatch) {
            return option;
          }

          return {
            ...option,
            options: option.options.filter((subOption) =>
              matchesQuery(subOption.label),
            ),
          };
        }
        return option;
      });
  }, [searchable, query, options]);

  const flattenedOptions = isGrouped
    ? filteredOptions.flatMap((group) =>
        group.options
          ? [
              { ...group, isGroupLabel: true },
              ...group.options.map((opt) => ({
                ...opt,
                groupLabel: group.label,
              })),
            ]
          : [group],
      )
    : filteredOptions;

  const isTruncated =
    maxRenderedOptions > 0 && flattenedOptions.length > maxRenderedOptions;
  const renderedOptions = isTruncated
    ? flattenedOptions.slice(0, maxRenderedOptions)
    : flattenedOptions;

  const handleClear = onClear || (isClearable ? () => onChange?.(null) : null);
  const showClear = Boolean(value) && handleClear;

  const handleSelect = (option) => {
    onChange?.(option);
  };

  const inputClass =
    "relative w-full overflow-hidden cursor-default rounded-md ring-1 text-left shadow-sm bg-white";
  const optionBaseClass =
    "relative cursor-default select-none py-2 px-4 pl-10 text-gray-900 data-[focus]:bg-blue-50 data-[focus]:text-blue-900";
  const iconBaseClass = "absolute inset-y-0 left-0 flex items-center pl-3";
  const actionBaseClass =
    "relative w-full text-left cursor-pointer select-none py-2 px-4 pl-10 enabled:hover:bg-blue-50 text-gray-900 disabled:cursor-not-allowed";

  // Without a text box to type into, a Combobox cannot be reached with Tab or
  // stepped through with the arrow keys, so a plain picker is a Listbox. Both
  // share the option list below.
  const Root = searchable ? Combobox : Listbox;
  const Options = searchable ? ComboboxOptions : ListboxOptions;
  const Option = searchable ? ComboboxOption : ListboxOption;
  const rootProps = searchable
    ? { immediate: true, onClose: () => setQuery("") }
    : {};

  const labelId = Boolean(label) ? `${id}-label` : undefined;
  const hintId = Boolean(hint) ? `${id}-hint` : undefined;

  const indicator = isLoading ? (
    <span className="flex space-x-1 items-center pr-1">
      <span
        className="w-1 h-1 rounded-full bg-gray-400 animate-bounce"
        style={{ animationDelay: "0ms" }}
      ></span>
      <span
        className="w-1 h-1 rounded-full bg-gray-400 animate-bounce"
        style={{ animationDelay: "150ms" }}
      ></span>
      <span
        className="w-1 h-1 rounded-full bg-gray-400 animate-bounce"
        style={{ animationDelay: "300ms" }}
      ></span>
    </span>
  ) : (
    !isDisabled && (
      <ChevronDownIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
    )
  );

  return (
    <div>
      {Boolean(label) && (
        <FormLabel id={labelId} htmlFor={id} required={required} hint={hint}>
          {t(label)}
        </FormLabel>
      )}
      <Root
        disabled={isDisabled}
        value={value}
        onChange={handleSelect}
        by="value"
        {...rootProps}
      >
        <div className={clsx("relative", Boolean(label) ? "mt-1" : "")}>
          <div
            className={clsx(
              inputClass,
              // A Listbox moves focus into its open list, so the ring follows
              // the open state as well as focus.
              hasError
                ? "ring-red-300 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500 has-[[data-open]]:ring-2 has-[[data-open]]:ring-red-500"
                : "ring-gray-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 has-[[data-open]]:ring-2 has-[[data-open]]:ring-blue-500",
              isDisabled && "cursor-not-allowed bg-gray-50 ring-gray-200",
            )}
          >
            {searchable ? (
              <ComboboxInput
                // The label points at this id; without it the combobox has no
                // accessible name.
                id={id}
                disabled={isDisabled}
                className={`w-full border-none py-2 pl-3 ${showClear ? "pr-14" : "pr-10"} text-sm leading-5 text-gray-900 focus:ring-0 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500`}
                displayValue={(option) => t(option?.label) || ""}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t(placeholder)}
                aria-describedby={hintId}
              />
            ) : (
              // Named "<label> <value>", the way a native select reads.
              <ListboxButton
                as={LabelledListboxButton}
                id={id}
                labelledBy={labelId && `${labelId} ${id}-value`}
                describedBy={hintId}
                disabled={isDisabled}
                className={clsx(
                  "relative w-full cursor-default py-2 pl-3 pr-10 text-left text-sm focus:outline-none disabled:cursor-not-allowed disabled:text-gray-500",
                  value ? "text-gray-900" : "text-gray-500",
                )}
              >
                <span id={`${id}-value`}>{t(value?.label || placeholder)}</span>
              </ListboxButton>
            )}

            {showClear && !isLoading && (
              <div className="absolute inset-y-0 right-0 pr-8 flex items-center">
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700 data-[disabled]:opacity-50"
                >
                  <span className="sr-only">{t("common.clear")}</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            )}
            {searchable ? (
              // Kept free of the field label on purpose: the text box beside it
              // already carries that name, and repeating it here would make a
              // second control answer to the same label.
              <ComboboxButton
                disabled={isDisabled}
                aria-label={t("options.show")}
                className="absolute inset-y-0 right-0 flex items-center pr-2 disabled:cursor-not-allowed"
              >
                {indicator}
              </ComboboxButton>
            ) : (
              // The whole field is already the button; this is only the icon.
              <span
                className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
                aria-hidden="true"
              >
                {indicator}
              </span>
            )}
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {isLoading ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-900">
                  {t("common.loading")}...
                </div>
              ) : !options?.length ||
                (filteredOptions.length === 0 && query !== "") ? (
                <>
                  <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                    {!options?.length
                      ? t("options.notAvailable")
                      : t("options.nothing")}
                  </div>
                  {allowAdd && onAdd && query && (
                    <>
                      <button
                        className={actionBaseClass}
                        onClick={() => onAdd(query)}
                        disabled={isAdding}
                      >
                        {isAdding ? (
                          <>
                            <span className="block truncate text-gray-500">
                              {t("common.adding")}...
                            </span>
                            <span className={`${iconBaseClass} text-blue-600`}>
                              <svg
                                className="animate-spin h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="block truncate">
                              <span className="text-blue-600">
                                {t("common.add")}
                              </span>{" "}
                              "{query}"
                            </span>
                            <span className={`${iconBaseClass} text-blue-600`}>
                              <PlusIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          </>
                        )}
                      </button>
                      {addError && (
                        <div className="px-4 py-2 text-red-600 text-sm">
                          <InputError error={addError} />
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  {renderedOptions.map((option, index) => {
                    if (option.isGroupLabel) {
                      return (
                        <div
                          key={`group-${option.label}-${index}`}
                          className="relative cursor-default select-none py-2 px-4 text-sm font-semibold text-gray-700 bg-gray-50 border-b border-gray-200"
                        >
                          {t(option.label)}
                        </div>
                      );
                    }

                    return (
                      <Option
                        key={option.value}
                        className={optionBaseClass}
                        value={option}
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`block truncate ${
                                selected ? "font-medium" : "font-normal"
                              }`}
                            >
                              {t(option.label)}
                            </span>
                            {selected && (
                              <span
                                className={`${iconBaseClass} text-blue-600`}
                              >
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </>
                        )}
                      </Option>
                    );
                  })}

                  {isTruncated && (
                    <div className="relative cursor-default select-none py-2 px-4 text-xs text-gray-500 border-t border-gray-100">
                      {t("options.refineToSeeMore")}
                    </div>
                  )}

                  {hasMore && (
                    <>
                      <button
                        className={actionBaseClass}
                        onClick={onLoadMore}
                        disabled={loadingMore}
                      >
                        {loadingMore ? (
                          <>
                            <span className="block truncate text-gray-500">
                              {t("common.loadingMore")}...
                            </span>
                            <span className={`${iconBaseClass} text-blue-600`}>
                              <svg
                                className="animate-spin h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="block truncate text-blue-600">
                              {t("common.loadMore")}
                            </span>
                            <span className={`${iconBaseClass} text-blue-600`}>
                              <ArrowDownIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          </>
                        )}
                      </button>
                      {loadMoreError && (
                        <div className="px-4 py-2 text-red-600 text-sm">
                          <InputError error={loadMoreError} />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </Options>
          </Transition>
        </div>
      </Root>
      {hasError && <InputError id={id} error={error} />}
    </div>
  );
};

export default FormSelect;
